import { useCallback, useEffect, useMemo, useState } from "react"
import classNames from "classnames"
import Loader from "@/components/Loader"
import Grid from "@/components/shared/Grid"
import GridItem from "@/components/shared/Grid/GridItem"
import { Icon, IconColor, IconProps } from "@/components/shared/Icon"
import { SpinnerSize } from "@/components/shared/Spinner"
import TextInput from "@/components/shared/TextInput"
import Tooltip, { TooltipPosition } from "@/components/shared/Tooltip"
import { TestId } from "@/constants/globals"
import "./index.scss"

type DelayProps = {
   loading: boolean
   delayPeriod: number
   title: string
   description: string
   minimumDelayPeriod: number
   iconName: IconProps["name"]
   onSave: (value: number) => void
}

function Delay({
   loading,
   delayPeriod,
   title,
   description,
   minimumDelayPeriod,
   iconName,
   onSave,
}: DelayProps) {
   const [isEditing, setIsEditing] = useState(false)
   const [delayPeriodValue, setDelayPeriodValue] = useState<number>(delayPeriod)
   const delayPeriodValid = useMemo(
      () => delayPeriodValue >= minimumDelayPeriod,
      [delayPeriodValue, minimumDelayPeriod]
   )

   const isFormChanged = useMemo(
      () => delayPeriod !== delayPeriodValue,
      [delayPeriod, delayPeriodValue]
   )

   useEffect(() => {
      setDelayPeriodValue(delayPeriod)
   }, [delayPeriod])

   const handleSave = useCallback(() => {
      if (isFormChanged) {
         onSave(delayPeriodValue)
         setIsEditing(false)
      }
   }, [isFormChanged, onSave, delayPeriodValue])

   const handleDiscard = useCallback(() => {
      setDelayPeriodValue(delayPeriod)
      setIsEditing(false)
   }, [delayPeriod])

   return (
      <>
         {loading ? (
            <GridItem size={3}>
               <div className="alert-setting-delay--loading">
                  <Loader
                     size={SpinnerSize.SMALL}
                     fullHeight={false}
                  />
               </div>
            </GridItem>
         ) : (
            <Grid columnGap={0}>
               <GridItem size={2.5}>
                  <div className="alert-setting-delay__section">
                     <span className="alert-setting-delay__section-title-container">
                        <Icon
                           name={iconName}
                           color={IconColor.HONOLULU_BLUE}
                        />
                        <span className="alert-setting-delay__section-title">
                           {title}
                        </span>
                     </span>
                     <div className="alert-setting-delay__section-subtitle">
                        {description}
                     </div>
                  </div>
               </GridItem>
               <GridItem
                  className="alert-setting-delay__content"
                  size={1.5}
               >
                  <div className="alert-setting-delay__input-container">
                     {isEditing ? (
                        <Tooltip
                           content={
                              delayPeriodValue < minimumDelayPeriod
                                 ? `Should be ${minimumDelayPeriod} or higher`
                                 : ""
                           }
                           open={delayPeriodValue < minimumDelayPeriod}
                           position={TooltipPosition.RIGHT}
                        >
                           <TextInput
                              type="number"
                              value={String(delayPeriodValue ?? "")}
                              onChange={(e) => {
                                 const v =
                                    e.target.value === ""
                                       ? ""
                                       : Number(e.target.value)
                                 setDelayPeriodValue(v as number)
                              }}
                              className="alert-setting-delay__input"
                           />
                        </Tooltip>
                     ) : (
                        <span
                           className={classNames("alert-setting-delay__time")}
                        >
                           {typeof delayPeriod === "number" &&
                           !Number.isNaN(delayPeriod)
                              ? delayPeriod
                              : "For X seconds"}
                        </span>
                     )}
                  </div>
                  <div className="alert-setting-delay__icons-container">
                     {isEditing ? (
                        <>
                           <Tooltip
                              key="cancel-tooltip"
                              content="Cancel"
                           >
                              <Icon
                                 name="cross"
                                 size="16"
                                 color={IconColor.HONOLULU_BLUE}
                                 onClick={handleDiscard}
                                 data-testid={TestId.ALERT_SETTING_CANCEL_ICON}
                              />
                           </Tooltip>
                           <Tooltip
                              key="save-tooltip"
                              content="Save"
                           >
                              <Icon
                                 name="save"
                                 size="16"
                                 color={
                                    isFormChanged
                                       ? IconColor.HONOLULU_BLUE
                                       : IconColor.SILVER
                                 }
                                 onClick={handleSave}
                                 disabled={!delayPeriodValid || !isFormChanged}
                                 data-testid={TestId.ALERT_SETTING_SAVE_ICON}
                              />
                           </Tooltip>
                        </>
                     ) : (
                        <Tooltip
                           key="edit-tooltip"
                           content="Edit"
                        >
                           <Icon
                              name="edit"
                              size="16"
                              color={IconColor.HONOLULU_BLUE}
                              onClick={() => setIsEditing(true)}
                              data-testid={TestId.ALERT_SETTING_EDIT_ICON}
                           />
                        </Tooltip>
                     )}
                  </div>
               </GridItem>
               <GridItem size={8} />
            </Grid>
         )}
      </>
   )
}

export default Delay
